import React, { useEffect, useRef } from "react";
import "./VoiceActionMenu.scss";

const KeyboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2.5" y="6" width="19" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M6 10.2h.01M9.5 10.2h.01M13 10.2h.01M16.5 10.2h.01M6 14h12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="2.5" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M5.5 11v.5a6.5 6.5 0 0 0 13 0V11M12 18v3.5M9 21.5h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Two compact floating actions instead of a true radial layout — a radial
// arrangement risks clipping/overlap on narrow phone widths depending on
// where the orb sits on screen; a simple row reads just as clearly and is
// far more robust across viewport sizes (per the redesign's own note that
// radial isn't a hard requirement). Each button animates in/out from the
// orb's own center (see .scss) rather than a plain conditional-render
// swap, and this component owns its own outside-click/touch/scroll/Escape
// close handling so SearchHero doesn't need to know about that at all.
const VoiceActionMenu = ({ open, onMic, onKeyboard, onRequestClose }) => {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onRequestClose?.();
      }
    };

    const handleScroll = () => onRequestClose?.();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onRequestClose?.();
    };

    // pointerdown (not click) so it also closes on a touch-drag/scroll
    // gesture start, and capture phase so it fires before the tap that
    // opened it can be misread as an immediate re-close on the same event.
    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("scroll", handleScroll, { capture: true });
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onRequestClose]);

  return (
    <div
      className={`voice-action-menu ${open ? "is-open" : ""}`}
      aria-hidden={!open}
      ref={rootRef}
    >
      <button
        type="button"
        className="voice-action-menu__btn voice-action-menu__btn--keyboard"
        onClick={onKeyboard}
        tabIndex={open ? 0 : -1}
        aria-label="Ввести текстом"
      >
        <KeyboardIcon />
      </button>

      <button
        type="button"
        className="voice-action-menu__btn voice-action-menu__btn--mic"
        onClick={onMic}
        tabIndex={open ? 0 : -1}
        aria-label="Голосовий пошук"
      >
        <MicIcon />
      </button>
    </div>
  );
};

export default VoiceActionMenu;
